/**
 * Code sourced from https://github.com/arunredhu/vuejs_boilerplate/blob/master/src/app/shared/services/app-logger/app-logger.js
 * on 24/10/2022 9:52 AM
 */

/* eslint no-console: ["off"] */
import toast from '@services/toasts/index.js'
import { nanoid } from 'nanoid'
import { createTraceLogger } from './TraceLogger'
import store from '@/store'
import config from '@/common/config'
import VueErrorDTO from '@/models/error/vueErrorDTO'
import WindowErrorDTO from '@/models/error/windowErrorDTO'
import Environment from '@/constants/core/Environment'
import { compileCustomProperties } from '@/helpers/log-helper'
import PromiseRejectionError from '@/models/error/promiseRejectionError'
import dayjs from '@/services/date'
import ErrorResponseDTO from '@/models/error/errorResponseDTO'

/**
 * @description Centralized logging service for the application.
 * In development, logs are routed to the console.
 * In production, logs are routed to Application Insights via traceLogger.
 */
class AppLogger {
  constructor(appInsights) {
    this.appInsights = appInsights
    this.initLogger()
  }

  /**
   * Initializes logging methods based on environment.
   * - In development: uses console methods.
   * - In production: uses Application Insights trace logging.
   */
  initLogger() {
    const env = config.get('env')

    switch (env) {
      case Environment.production:
        this.initProductionLogger()
        break

      case Environment.development:
      default:
        this.initDevelopmentLogger()
        break
    }
  }

  initDevelopmentLogger() {
    // Development logging: use native console methods
    this.log = console.log.bind(console)
    this.debug = console.debug.bind(console)
    this.info = console.info.bind(console)
    this.warn = console.warn.bind(console)
    this.error = console.error.bind(console)
    this.critical = console.error.bind(console)

    /**
     * Logs structured error information to Vuex and optionally toasts.
     */
    this.logException = (err) => {
      if (!err) return this.warn('AppLogger: Error received was empty')

      this.error(err.error, err)

      const message = `${err.error?.name ?? 'Error'}: ${err.error?.message}`

      store.commit('ADD_DEBUG_MESSAGE', {
        exception: err.error,
        date: dayjs().format('llll'),
        message,
        id: nanoid(),
      })
      if (store.getters.isDebugModeActive) toast.debug(message)
    }
  }

  initProductionLogger() {
    const enrichProperties = (properties) =>
      compileCustomProperties(store, {
        ...properties,
        route: window.location.pathname,
        env: config.get('env'),
      })

    this.logTrace = createTraceLogger(this.appInsights, enrichProperties)

    this.log = (message, properties = {}) =>
      this.logTrace.verbose(message, properties)
    this.debug = (message, properties = {}) =>
      this.logTrace.verbose(message, properties)
    this.info = (message, properties = {}) =>
      this.logTrace.info(message, properties)
    this.warn = (message, properties = {}) =>
      this.logTrace.warn(message, properties)
    this.error = (message, properties = {}) =>
      this.logTrace.error(message, properties)
    this.critical = (message, properties = {}) =>
      this.logTrace.critical(message, properties)

    /**
     * Logs structured error information to Application Insights.
     * Handles multiple error DTO types and enriches telemetry.
     */
    this.logException = (err) => {
      let errorDetails

      switch (true) {
        case err instanceof VueErrorDTO:
          errorDetails = {
            info: err.info,
            route: err.vm?.$route?.name,
            component: err.vm?.$options?.name || 'UnnamedComponent',
          }
          break

        case err instanceof WindowErrorDTO:
          errorDetails = err
          break

        // NB: `PromiseRejectionEvent` is a browser-only global object.
        //     The below type check achieves the same result as
        //     `instanceof PromiseRejectionEvent` but allows it to be testable
        //     in Node.js environments.
        case err?.type === 'unhandledrejection' && 'reason' in err:
          errorDetails = {
            reason: err?.reason,
            type: err?.type,
          }
          err = new PromiseRejectionError(err.reason)
          break

        case err instanceof ErrorResponseDTO:
          errorDetails = {
            errorResponse: {
              source: err.source,
              type: err.type,
              message: err.message,
            },
          }
          break

        default:
          errorDetails = {
            error: err,
          }
          break
      }

      const properties = compileCustomProperties(store, errorDetails, true)

      this.appInsights.trackException({
        exception: err?.error || err,
        properties,
      })
    }
  }
}

let loggerInstance = null

const getLogger = (appInsights) => {
  if (!loggerInstance) {
    loggerInstance = new AppLogger(appInsights)
  }
  return loggerInstance
}

export { getLogger, AppLogger }
